import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom"; 
import { X } from 'lucide-react';
import "./Myaccount.css";

const Dashboard = () => {
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState("address");
  const [canAccessInternship, setCanAccessInternship] = useState(false);
  const [internshipDetails, setInternshipDetails] = useState(null);
  const [amount, setAmount] = useState(0); 
  const [paymentDone, setPaymentDone] = useState(false);
  const [slotDetails, setSlotDetails] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
   const [message, setMessage] = useState(null);
  
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState({ address1: null, address2: null });
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    fullname: "",
    mobile: "",
    pincode: "",
    flat: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    default: false,
    instructions: "",
  });

  const [formErrors, setFormErrors] = useState({
  fullname: "",
  mobile: "",
  pincode: "",
  flat: "",
  area: "",
  landmark: "",
  city: "",
  state: "",
  instructions: "",
});

  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error("Error loading config:", err));
  }, []);

   useEffect(() => {
      if (message) {
        const timer = setTimeout(() => setMessage(null), 5000);
        return () => clearTimeout(timer);
      }
    }, [message]);
  

  const validateField = (name, value) => {
  let error = "";

  switch (name) {
    case "fullname":
      if (!value.trim()) error = "Full name is required";
      else if (!/^[A-Za-z\s]+$/.test(value)) error = "Full name should contain only letters";
      break;
    case "mobile":
      if (!/^\d{10}$/.test(value)) error = "Enter a valid 10-digit mobile number";
      break;
    case "pincode":
      if (!/^\d{6}$/.test(value)) error = "Enter a valid 6-digit pincode";
      break;
    case "flat":
      if (!value.trim()) error = "Flat/House No. is required";
      break;
    case "area":
      if (!value.trim()) error = "Area/Street is required";
      break;
    case "landmark":
      if (!value.trim()) error = "Landmark is required";
      break;
    case "city":
      if (!value.trim()) error = "City is required";
      break;
    case "state":
      if (!value.trim()) error = "State is required";
      break;
    case "instructions":
      if (!value.trim()) error = "Delivery instructions are required";
      break;
    default:
      break;
  }

  setFormErrors(prev => ({ ...prev, [name]: error }));
};


  useEffect(() => {
    if (!config) return;

    const fetchUserData = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}${config.USER_INFO}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (data.success) {
          setUsername(data.fullname);
           setUserEmail(data.email);
          setAddresses({
            address1: data.address1 || null,
            address2: data.address2 || null,
          });
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }

      try {
        const res2 = await fetch(`${config.BASE_URL}${config.CHECK_INTERNSHIP}`, {
          method: "POST",
          credentials: "include",
        });
        const data2 = await res2.json();
        if (data2.success) {
          setCanAccessInternship(data2.approved);
          if (data2.applications && data2.applications.length > 0) {
            const details = data2.applications[0];
            setAmount(details.amount || 0); 
            setInternshipDetails(details); 
          }
        }
      } catch (err) {
        console.error("Error checking internship:", err);
      }
    };

    fetchUserData();
  }, [config]);

    const handleLogout = async () => {
    if (!config) return;
    try {
      const res = await fetch(`${config.BASE_URL}${config.USER_LOGOUT}`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.status) {
        setMessage("Logged out successfully");
        navigate("/"); 
      } else {
        setMessage("Logout failed");
      }
    } catch (err) {
      console.error("Logout error:", err);
      setMessage("Error logging out");
    }
  };

  useEffect(() => {
    if (activeTab === "internship" && config) {
      checkSlot();
    }
  }, [activeTab, config]);

  const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  const fieldValue = type === "checkbox" ? checked : value;
  setFormData({ ...formData, [name]: fieldValue });
  validateField(name, fieldValue); 
};


  const handleSave = async (e) => {
    e.preventDefault();

      const requiredFields = [
  "fullname",
  "mobile",
  "pincode",
  "flat",
  "area",
  "landmark",
  "city",
  "state",
  "instructions",
];

  const hasEmpty = requiredFields.some(f => !formData[f].trim());
  if (hasEmpty) {
    setMessage("Please fill all required fields.");
    return;
  }

     const hasErrors = Object.values(formErrors).some(err => err);
  if (hasErrors) {
    setMessage("Please fix the errors before saving.");
    return;
  }
    const slot = editingAddress === 1 ? "address1" : "address2";
    const payload = { ...formData, slot };
    try {
      const res = await fetch(`${config.BASE_URL}${config.SAVE_ADDRESS}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setAddresses({ ...addresses, [slot]: data.address });
        setEditingAddress(null);
      } else {
        setMessage(data.message || "Failed to save address");
      }
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  const handlePayment = async () => {
    if (!config) return;
    try {
      const amountInPaise = Math.round(amount * 100);

      const response = await fetch(`${config.BASE_URL}${config.CREATE_ORDER}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount: amountInPaise }),
      });

      const data = await response.json();
      const { order_id } = data;

      if (!order_id) {
        setMessage("Failed to create Razorpay order");
        return;
      }

      const options = {
        key: "rzp_live_1P7gGwYcHvQ4oz",
        amount: amountInPaise,
        currency: "INR",
        name: "Robocoupler",
        description: "Internship Payment",
        image: "/tm logo.png",
        order_id,
        handler: async function (response) {
          const verifyRes = await fetch(
            `${config.BASE_URL}${config.INTERN_VERIFY_PAYMENT}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                ...response,                       
                intern_id: internshipDetails.intern_id,
                name: internshipDetails.name,
                domain: internshipDetails.domain,
                amount: amount
              }),
            }
          );

          const verifyData = await verifyRes.json();
          if (verifyData.status) {
            setMessage(" Payment successful!");
          } else {
            setMessage(" Payment verification failed");
          }
        },
       prefill: {
    name: username || "User", 
    email: userEmail || "customer@example.com", 
    contact: internshipDetails?.mobile || "9999999999", 
  },
  theme: { color: "#0a7cff" },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error("Payment error:", err);
      setMessage("Something went wrong. Please try again.");
    }
  };

  const checkSlot = async () => {
    try {
      const res = await fetch(`${config.BASE_URL}${config.GET_SLOT_DETAILS}`, {
        method: "POST",
        credentials: "include"
      });
      const data = await res.json();

      if (data.success) {
        if (data.status === "in_process") {
          setPaymentDone(true);
          setSlotDetails(null);
        } else if (data.status === "allocated") {
          setPaymentDone(true);
          setSlotDetails(data.slot);
        }
      }
    } catch (err) {
      console.error("Error checking slot:", err);
    }
  };

  // Render main content
  const renderContent = () => {
    if (activeTab === "address") {
      return (
        <>
          {[1, 2].map(num => {
            const slot = num === 1 ? "address1" : "address2";
            const label = num === 1 ? "Shipping Address" : "Billing Address";
            return (
              <div key={num} className="address-block">
                {!addresses[slot] && editingAddress !== num && (
                  <button className="add-address-btn" onClick={() => setEditingAddress(num)}>
                    + Add {label}
                  </button>
                )}
                {editingAddress === num ? (
                  <form className="address-form" onSubmit={handleSave}>
  <h3>{addresses[slot] ? "Edit" : "Add"} {label}</h3>

  <div className="form-row">
    <label htmlFor={`fullname-${num}`}>Full Name</label>
    <input
      id={`fullname-${num}`}
      type="text"
      name="fullname"
      placeholder="Enter your full name"
      className="form-input"
      value={formData.fullname}
      onChange={handleChange}
      required
    />
    {formErrors.fullname && (
    <p className="error-message">{formErrors.fullname}</p>
  )}
  </div>

  <div className="form-row">
    <label htmlFor={`mobile-${num}`}>Mobile Number</label>
    <input
      id={`mobile-${num}`}
      type="text"
      name="mobile"
      placeholder="Enter mobile number"
      className="form-input"
      value={formData.mobile}
      onChange={handleChange}
       maxLength={10}       
  pattern="\d{10}"
      required
    />
    {formErrors.mobile && (
    <p className="error-message">{formErrors.mobile}</p>
  )}
  </div>

  <div className="form-row">
    <label htmlFor={`pincode-${num}`}>Pincode</label>
    <input
      id={`pincode-${num}`}
      type="text"
      name="pincode"
      placeholder="Enter pincode"
      className="form-input"
      value={formData.pincode}
      onChange={handleChange}
       maxLength={6}        
  pattern="\d{6}"
      required
    />
    {formErrors.pincode && <p className="error-message">{formErrors.pincode}</p>}
  </div>

  <div className="form-row">
    <label htmlFor={`flat-${num}`}>Flat / House No.</label>
    <input
      id={`flat-${num}`}
      type="text"
      name="flat"
      placeholder="Enter flat/house number"
      className="form-input"
      value={formData.flat}
      onChange={handleChange}
      required
    />
     {formErrors.flat && <p className="error-message">{formErrors.flat}</p>}
  </div>

  <div className="form-row">
    <label htmlFor={`area-${num}`}>Area / Street</label>
    <input
      id={`area-${num}`}
      type="text"
      name="area"
      placeholder="Enter area/street"
      className="form-input"
      value={formData.area}
      onChange={handleChange}
      required
    />
    {formErrors.area && <p className="error-message">{formErrors.area}</p>}
  </div>

  <div className="form-row">
    <label htmlFor={`landmark-${num}`}>Landmark</label>
    <input
      id={`landmark-${num}`}
      type="text"
      name="landmark"
      placeholder="Enter landmark"
      className="form-input"
      value={formData.landmark}
      onChange={handleChange}
      required
    />
     {formErrors.flat && <p className="error-message">{formErrors.flat}</p>}
  </div>

  <div className="form-row">
    <label htmlFor={`city-${num}`}>City</label>
    <input
      id={`city-${num}`}
      type="text"
      name="city"
      placeholder="Enter city"
      className="form-input"
      value={formData.city}
      onChange={handleChange}
      required
    />
     {formErrors.city && <p className="error-message">{formErrors.city}</p>}
  </div>

  <div className="form-row">
    <label htmlFor={`state-${num}`}>State</label>
    <input
      id={`state-${num}`}
      type="text"
      name="state"
      placeholder="Enter state"
      className="form-input"
      value={formData.state}
      onChange={handleChange}
      required
    />
     {formErrors.state && <p className="error-message">{formErrors.state}</p>}
  </div>

  <div className="form-row">
    <label htmlFor={`instructions-${num}`}>Delivery Instructions</label>
    <textarea
      id={`instructions-${num}`}
      name="instructions"
      placeholder="Enter any delivery instructions"
      className="form-input"
      value={formData.instructions}
      onChange={handleChange}
      required
    />
     {formErrors.instructions && <p className="error-message">{formErrors.instructions}</p>}
  </div>

  <div className="form-row checkbox">
    <input
      type="checkbox"
      name="default"
      id={`default-${num}`}
      checked={formData.default}
      onChange={handleChange}
    />
    <label htmlFor={`default-${num}`}>Set as default address</label>
  </div>

  <button type="submit" className="save-btn">
    Save 
  </button>
</form>

                ) : addresses[slot] ? (
                  <div className="saved-address">
                    <h3>{label}</h3>
                    <p>{addresses[slot]}</p>
                    <button className="edit-btn" onClick={() => setEditingAddress(num)}>Edit</button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </>
      );
    }

    if (activeTab === "internship" && canAccessInternship) {
      return (
        <div className="tab-internship-page">
          {!paymentDone && (
            <>
              <h2>Your internship is approved!</h2>
              <p>To continue, please proceed with the payment below.</p>
              <button className="proceed-payment-btn" onClick={handlePayment}>
                Proceed to Payment
              </button>
            </>
          )}

          {paymentDone && !slotDetails && (
            <>
              <h2>Application in Process</h2>
              <p>Your payment has been received. Your internship slot will be allocated soon.</p>
            </>
          )}

          {paymentDone && slotDetails && (
  <div className="tab-internship-page">
    <h2>🎉 Slot Confirmed!</h2>
    <p>Your internship slot has been successfully allocated.</p>

    <div className="slot-info">
      <p><b>Slot No:</b> {slotDetails.slot_no}</p>
      <p><b>Domain:</b> {slotDetails.domain}</p>
      <p><b>Days:</b> {slotDetails.days}</p>
      <p><b>Timings:</b> {slotDetails.timings}</p>
    </div>
  </div>
)}

        </div>
      );
    }

    return <p>Content not available.</p>;
  };

  return (
    <div className="acc-dashboard-container">
      <header className="acc-dashboard-header">
        <h1>{loading ? "Loading..." : `Hello ${username || "User"}`}</h1>
      </header>

      <div className="acc-dashboard-layout">
        <aside className="acc-sidebar">
          <h2>Account</h2>
          <a onClick={() => setActiveTab("address")}>My Address</a>
          {canAccessInternship && (
            <a onClick={() => setActiveTab("internship")}>Internship</a>
          )}
          <a onClick={() =>setShowLogoutConfirm(true)} className="logout-btn">
          Logout
        </a>

        </aside>

        <main className="acc-main-content">{renderContent()}</main>
      </div>

            {showLogoutConfirm && (
  <div 
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 2000,
    }}
  >
    <div 
      style={{
        background: "white",
        padding: "20px 30px",
        borderRadius: "12px",
        maxWidth: "400px",
        textAlign: "center",
        boxShadow: "0 6px 20px rgba(0,0,0,0.3)"
       
      }}
    >
      <h3 style={{ marginBottom: "15px" }}>Hello, Human!</h3>
      <p style={{ marginBottom: "20px" }}>Logging out will end our connection. Proceed?</p>
      
      <div style={{ display: "flex", justifyContent: "space-around" }}>
        <button 
          className="premium-glass-button" 
          style={{ color: "black" }}
          onClick={() => setShowLogoutConfirm(false)}
        >
          Cancel
        </button>
        <button 
          className="premium-glass-button" 
          style={{ color: "black" }}
          onClick={() => {
            handleLogout();
            setShowLogoutConfirm(false);
          }}
        >
          Yes, Logout
        </button>
      </div>
    </div>
  </div>
)}
      {message && (
        <div
          style={{
            position: "fixed",
            top: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "15px 20px",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            color: "black",
            fontSize: "14px",
            fontWeight: "500",
            zIndex: 1000,
            minWidth: "250px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <span>{message}</span>
          <span
            style={{ marginLeft: "15px", cursor: "pointer" }}
            onClick={() => setMessage(null)}
          >
            ×
          </span>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
